/**
 * MotivationalReviewView — Summary of All Answers
 *
 * Read-only view shown before final submission.
 * Displays all answered questions with their text, allowing the user
 * to review and click "Edit" to jump back to a specific question.
 *
 * @module copilot/widgets/motivational-form
 */

import { cn } from '@tonnie/foundation';
import { useTranslation, useLocale } from '@tonnie/locale';
import { Badge, Button, Card, CardContent } from '@tonnie/ui';
import { CheckCircle2, Edit, AlertTriangle } from '@tonnie/assets';
import { motion } from 'framer-motion';

import type { IQuestion, IValidationResult } from './motivational-form.types';
import { countWords, validationMessage } from './motivational-form.utils';

interface MotivationalReviewViewProps {
  questions: IQuestion[];
  answers: Record<string, string>;
  validationResults: Record<string, IValidationResult>;
  /** Jump back to a specific question index */
  onEditQuestion: (index: number) => void;
}

export function MotivationalReviewView({
  questions,
  answers,
  validationResults,
  onEditQuestion,
}: MotivationalReviewViewProps) {
  const { t } = useTranslation();
  const { formatNumber } = useLocale();
  const answeredCount = questions.filter((q) => answers[q.questionId]?.trim().length > 0).length;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="space-y-4"
    >
      {/* Summary header */}
      <div className="text-center mb-6">
        <div className="mx-auto w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mb-3">
          <CheckCircle2 className="w-7 h-7 text-primary-accessible" />
        </div>
        <h3 className="text-lg font-bold text-foreground mb-1">{t('copilot.reviewYourAnswers')}</h3>
        <p className="text-sm text-muted-foreground">
          {t('copilot.reviewDesc', { answered: answeredCount, total: questions.length })}
        </p>
      </div>

      {/* Answer cards */}
      {questions.map((q, index) => {
        const answer = (answers[q.questionId] || '').trim();
        const validation = validationResults[q.questionId];
        const hasAnswer = answer.length > 0;
        const wordCount = hasAnswer ? countWords(answer) : 0;
        const hasError = validation?.status === 'ERROR';
        const hasWarning = validation?.status === 'WARNING';

        return (
          <Card
            key={q.questionId}
            className={cn(
              'border-2 transition-colors',
              hasError
                ? 'border-destructive/30'
                : hasWarning
                  ? 'border-tone-amber-line'
                  : hasAnswer
                    ? 'border-success/30'
                    : 'border-border/50'
            )}
          >
            <CardContent className="p-4">
              {/* Question header row */}
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex items-center gap-2 min-w-0">
                  <div
                    className={cn(
                      'w-6 h-6 rounded-full flex items-center justify-center shrink-0 text-xs font-bold',
                      hasError
                        ? 'bg-destructive/10 text-destructive-accessible'
                        : hasAnswer
                          ? 'bg-success/10 text-success-accessible'
                          : 'bg-muted text-muted-foreground'
                    )}
                  >
                    {hasAnswer && !hasError ? <CheckCircle2 className="w-3.5 h-3.5" /> : index + 1}
                  </div>
                  <span className="text-sm font-medium text-foreground line-clamp-1">
                    {q.label}
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onEditQuestion(index)}
                  className="h-7 px-2 text-xs shrink-0 gap-1"
                >
                  <Edit className="w-3 h-3" />
                  {t('common.edit')}
                </Button>
              </div>

              {/* Answer preview */}
              {hasAnswer ? (
                <div className="pl-8">
                  <p className="text-sm text-muted-foreground line-clamp-3 leading-relaxed">
                    {answer}
                  </p>
                  <div className="flex items-center gap-3 mt-2">
                    <span className="text-xs text-muted-foreground">
                      {t('copilot.words', { count: wordCount })}
                    </span>
                    {(hasError || hasWarning) && (
                      <Badge
                        variant="outline"
                        className={cn(
                          'text-xs px-2 py-0.5 rounded-full',
                          hasError
                            ? 'bg-destructive/10 text-destructive-accessible border-destructive/30'
                            : 'bg-tone-amber-subtle text-tone-amber border-tone-amber-line'
                        )}
                      >
                        {hasError ? t('copilot.needsFix') : t('copilot.couldImprove')}
                      </Badge>
                    )}
                  </div>
                  {/* SF-039: surface the actionable reason (already localized in
                      `validationMessage`) so the badge tells the user WHAT to
                      improve — shown only for answers needing action. */}
                  {(hasError || hasWarning) && validation && (
                    <p
                      className={cn(
                        'mt-1.5 text-xs',
                        hasError ? 'text-destructive-accessible' : 'text-tone-amber'
                      )}
                    >
                      {validationMessage(validation, t, formatNumber)}
                    </p>
                  )}
                </div>
              ) : (
                <div className="pl-8 flex items-center gap-2">
                  <AlertTriangle className="w-3.5 h-3.5 text-tone-amber" />
                  <span className="text-xs text-muted-foreground italic">
                    {t('copilot.notAnsweredYet')}
                  </span>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </motion.div>
  );
}
